import React, { useState, FC, useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Button, Select, Input, Drawer, Form, Divider, Space } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import { RootState } from "../../../../Store/store";
import {
  setCallFilterDivisionList,
  setCallOfUsers,
  setCurrentPage,
  setIsDrawerOpen} from "../../../../Store/Reducers/inviteUser";
import { setCallMenuList } from "../../../../Store/Reducers/sideMenuBar";

import useLanguage from "../../../../Hooks/useLanguage";

// API
import {
  getResponsiblePersonList,
  getRoleList,
  getDivisionsList,
  addUser,
  addRole,
  addDivision} from "../../../../Network/Core/users";

//  helper
import {
  errorHandling,
  formatCEOText,
  handlePreventInputKeyDownAction,
  isScrolledToBottom,
  removeDuplicatesById,
  validateEmail,
  validateName} from "../../../../Library/Utils";

import {
  addUserPersonListDefaultPageSize,
  divisionListDefaultPageSize,
  roleListDefaultPageSize,
  defaultCurrentPage} from "../../../../Resources/Statics";

import { IOptions } from "../../../../types/options";

import ProfileImage from "../../../UI/Image";
import { usePermissions } from "../../../../Hooks/usePermissions";

interface AddNewUserInterface {
  addNewUserOpen: boolean;
}
interface personData {
  id: string;
  first_name: string;
  last_name: string;
  profile_image: string;
  profile_color_hash: string;
}
interface divisionData {
  id: string;
  division: {
    name: string;
  };
}
interface IRoleData {
  id: string;
  role: {
    id: string;
    name: string;
  };
}
interface AddUsers {
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  company_division: string;
  reporting_to: string;
  message?: string;
}
const AddNewUser: FC<AddNewUserInterface> = ({ addNewUserOpen }) => {
  const { Search, TextArea } = Input;
  const { Option } = Select;

  // Reporting Person
  const [personList, setPersonList] = useState([]);
  const [personListCurrentPage, setPersonListCurrentPage] = useState(1);
  const [personListloading, setPersonListLoading] = useState(false);
  const [searchTextOfPerson, setSearchTextOfPerson] = useState("");
  const [hasMorePerson, setHasMorePerson] = useState(true);
  const [callOfPersonList, setCallOfPersonList] = useState<boolean>(false);
  const [responsiblePerson, setResponsiblePerson] = useState("");
  // Role
  const [roleList, setRoleList] = useState<IRoleData[]>([]);
  const [newlyAddedRoles, setNewlyAddedRoles] = useState<IRoleData[]>([]);

  const [roleListCurrentPage, setRoleListCurrentPage] = useState(1);
  const [roleListloading, setRoleListLoading] = useState(false);
  const [searchTextOfRole, setSearchTextOfRole] = useState("");
  const [hasMoreRole, setHasMoreRole] = useState(true);
  const [callOfRoleList, setCallOfRoleList] = useState(true);
  const [role, setRole] = useState("");
  const [roleName, setRoleName] = useState("");
  const [newRoleID, setNewRoleID] = useState<string>("");
  const [newDivisionID, setNewDivisionID] = useState<string>("");
  // Division
  const [divisionList, setDivisionList] = useState<divisionData[]>([]);
  const [newlyAddedDivisions, setNewlyAddedDivisions] = useState<
    divisionData[]
  >([]);
  const [divisionListCurrentPage, setDivisionListCurrentPage] = useState(1);
  const [divisionListloading, setDivisionListLoading] = useState(false);
  const [searchTextOfDivision, setSearchTextOfDivision] = useState("");
  const [hasMoreDivision, setHasMoreDivision] = useState(true);
  const [callOfDivisionList, setCallOfDivisionList] = useState(true);
  const [division, setDivision] = useState("");
  const [divisionName, setDivisionName] = useState("");
  const [formDisabled, setFormDisabled] = useState<boolean>(false);
  const [isReportingToDisabled, setIsReportingToDisabled] =
    useState<boolean>(true);

  const [form] = Form.useForm();

  const dispatch = useDispatch();

  const { convertText } = useLanguage();

  const userDetails = useSelector(
    (state: RootState) => state.authReducer.userDetails
  );

  const { isCeo } = usePermissions();

  // Reporting Person functions //
  const onScrollOfPerson = async (event: React.UIEvent<HTMLElement>) => {
    if (hasMorePerson && !personListloading && isScrolledToBottom(event)) {
      setPersonListLoading(true);
      // event.target.scrollTo(0, event.target.scrollHeight);
      setPersonListCurrentPage(personListCurrentPage + 1);
      setCallOfPersonList(true);
    }
  };

  const getResponsiblePersonData = async (
    searchTerm: string = "",
    searchArg: boolean = false
  ) => {
    try {
      const response = await getResponsiblePersonList(
        searchArg ? 1 : personListCurrentPage,
        addUserPersonListDefaultPageSize,
        searchArg ? searchTerm : searchTextOfPerson,
        false,
        division,
        true,
        "first_name",
        true
      );
      if (personListCurrentPage !== 1) {
        let newDummyList = [...personList];
        newDummyList = newDummyList.concat(response?.data?.data?.results);
        setPersonList(newDummyList);
      } else {
        setPersonList(response?.data?.data?.results);
      }
      setHasMorePerson(response?.data?.data?.next !== null);
      setCallOfPersonList(false);
    } catch (error) {
      setPersonListLoading(false);
      setCallOfPersonList(false);
    } finally {
      setPersonListLoading(false);
      setCallOfPersonList(false);
    }
  };

  const onPersonSearch = (value: string) => {
    setPersonListCurrentPage(1);
    setSearchTextOfPerson(value);
    getResponsiblePersonData(value, true);
  };

  const onPersonChange = (value: string) => {
    setResponsiblePerson(value);
  };

  useEffect(() => {
    if (callOfPersonList && addNewUserOpen) {
      getResponsiblePersonData();
    }
  }, [callOfPersonList, addNewUserOpen]);

  const onRoleChange = (value: string) => {
    setRole(value);
  };

  // Roles functions //
  const getRolesData = async (searchText: string = "") => {
    try {
      const response = await getRoleList(userDetails?.company_id, {
        page: roleListCurrentPage,
        pageSize: roleListDefaultPageSize,
        search: searchText,
        sort_by: "name"});

      if (roleListCurrentPage !== 1) {
        const cleanRoles = removeDuplicatesById(
          [...roleList, ...(response?.data?.data?.results ?? [])],
          (item) => item.role.id
        );
        setRoleList(cleanRoles);
      } else {
        const cleanRoles = removeDuplicatesById(
          [...newlyAddedRoles, ...(response?.data?.data?.results ?? [])],
          (item) => item.role.id
        );
        setRoleList(cleanRoles);
      }

      setRoleListLoading(false);
      setHasMoreRole(response?.data?.data?.next !== null);
      setCallOfRoleList(false);

      if (newRoleID) {
        form.setFieldValue("role", newRoleID);
        onRoleChange(newRoleID);
        setNewRoleID("");
      }
    } catch (error) {
      setRoleListLoading(false);
      setCallOfRoleList(false);
    }
  };

  const onScrollOfRole = async (event: any) => {
    if (hasMoreRole && !roleListloading && isScrolledToBottom(event)) {
      setRoleListLoading(true);
      // event.target.scrollTo(0, event.target.scrollHeight);
      setRoleListCurrentPage(roleListCurrentPage + 1);
      getRolesData(searchTextOfRole);
    }
  };

  const onRoleSearch = (value: string) => {
    setRoleListCurrentPage(1);
    setSearchTextOfRole(value);
    getRolesData(value);
  };

  const addNewRole = async () => {
    try {
      setFormDisabled(true);
      const response = await addRole(userDetails?.company_id, {
        name: roleName});
      const newRole: IRoleData = {
        id: crypto.randomUUID(),
        role: response?.data?.data};
      setRoleList([...roleList, newRole]);
      setNewlyAddedRoles([...newlyAddedRoles, newRole]);
      toast.success(convertText("successAddRole"));
      setNewRoleID(response?.data?.data?.id);
      setRoleListCurrentPage(1);
      setCallOfRoleList(true);
      setRoleName("");
    } catch (error) {
      errorHandling(error);
    } finally {
      setFormDisabled(false);
    }
  };

  useEffect(() => {
    if (callOfRoleList && addNewUserOpen) {
      getRolesData();
    }
  }, [callOfRoleList, addNewUserOpen]);

  useEffect(() => {
    if (division) setIsReportingToDisabled(false);
    else setIsReportingToDisabled(true);
  }, [division]);

  // Division Function //

  const onScrollOfDivision = async (event: any) => {
    if (hasMoreDivision && !divisionListloading && isScrolledToBottom(event)) {
      setDivisionListLoading(true);
      // event.target.scrollTo(0, event.target.scrollHeight);
      setDivisionListCurrentPage(divisionListCurrentPage + 1);
      setCallOfDivisionList(true);
    }
    if (hasMoreRole && !roleListloading && isScrolledToBottom(event)) {
      setRoleListLoading(true);
      // event.target.scrollTo(0, event.target.scrollHeight);
      setRoleListCurrentPage(roleListCurrentPage + 1);
      getRolesData(searchTextOfRole);
    }
  };

  const onDivisionChange = (value: string) => {
    setDivision(value);
    setResponsiblePerson("");
    form.setFieldValue(`person`, null);
    setCallOfPersonList(true);
  };

  const getDivisionsData = async () => {
    try {
      const response = await getDivisionsList(userDetails?.company_id, {
        page: divisionListCurrentPage,
        pageSize: divisionListDefaultPageSize,
        search: searchTextOfDivision,
        sort_by: "division_name"});

      const apiData = response?.data?.data ?? {};
      const { results = [], next } = apiData;

      // Merge + remove duplicates
      if (divisionListCurrentPage !== 1) {
        const mergedDivisions = removeDuplicatesById(
          [...divisionList, ...results],
          (item) => item.id
        );
        setDivisionList(mergedDivisions);
      } else {
        const mergedDivisions = removeDuplicatesById(
          [...newlyAddedDivisions, ...results],
          (item) => item.id
        );
        setDivisionList(mergedDivisions);
      }
      setHasMoreDivision(Boolean(next));

      // Handle post-add division selection
      if (newDivisionID) {
        onDivisionChange(newDivisionID);
        form.setFieldValue("division", newDivisionID);
        setNewDivisionID("");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDivisionListLoading(false);
      setCallOfDivisionList(false);
    }
  };

  const onDivisionSearch = (value: string) => {
    setDivisionListCurrentPage(1);
    setSearchTextOfDivision(value);
    setCallOfDivisionList(true);
  };

  const addNewDivision = async () => {
    try {
      setFormDisabled(true);
      const response = await addDivision(userDetails?.company_id, {
        name: divisionName});
      toast.success(convertText("successAddDivision"));

      const cleanDivisions = removeDuplicatesById(
        [...divisionList, response?.data?.data],
        (item) => item.id
      );
      const cleanNewlyAddedDivisions = removeDuplicatesById(
        [...newlyAddedDivisions, response?.data?.data],
        (item) => item.id
      );
      setNewlyAddedDivisions(cleanNewlyAddedDivisions);
      setDivisionList(cleanDivisions);
      setNewDivisionID(response?.data?.data?.id);
      setDivisionListCurrentPage(1);
      setCallOfDivisionList(true);
      setDivisionName("");
      dispatch(setCallFilterDivisionList(true));
      setCallOfPersonList(true);
    } catch (error) {
      errorHandling(error);
    } finally {
      setFormDisabled(false);
    }
  };

  useEffect(() => {
    if (callOfDivisionList && addNewUserOpen) {
      getDivisionsData();
    }
  }, [callOfDivisionList, addNewUserOpen]);

  // Reset states
  const resetStates = () => {
    dispatch(setIsDrawerOpen(false));
    setRoleListCurrentPage(1);
    setDivisionListCurrentPage(1);
    setPersonListCurrentPage(1);
    setSearchTextOfDivision("");
    setSearchTextOfPerson("");
    setCallOfDivisionList(true);
    setCallOfRoleList(true);
    setCallOfPersonList(true);
    setHasMoreRole(true);
    setHasMoreDivision(true);
    setHasMorePerson(true);
    setRoleName("");
    setDivisionName("");
    setDivision("");
    setResponsiblePerson("");
    setIsReportingToDisabled(true);
    form.resetFields();
  };

  // Create new user
  const handleSubmit = async (values: {
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    division: string | IOptions;
    person: string;
    message: string;
  }) => {
    try {
      setFormDisabled(true);
      const {
        firstName,
        lastName,
        email,
        role: userRole,
        division: userDivision,
        message,
        person} = values;

      const payload: AddUsers = {
        first_name: firstName,
        last_name: lastName,
        email,
        role: userRole,
        company_division:
          typeof userDivision === "object" ? userDivision?.value : userDivision,
        reporting_to: isCeo ? person : userDetails?.id};
      if (message) {
        payload.message = message;
      }
      const response = await addUser(userDetails?.company_id, payload);
      toast.success(convertText("successAddUser"));
      dispatch(setCurrentPage(defaultCurrentPage));
      dispatch(setCallOfUsers(true));
      dispatch(setCallMenuList(true));
      resetStates();
    } catch (error) {
      errorHandling(error);
    } finally {
      setFormDisabled(false);
    }
  };

  const steps = [
    {
      content: (
        <div className="objective-step responsibility-step kpi-step">
          <div className="title-box">
            <p className="user_drawer_p">
              {convertText("addTeamMembersToYourFocuspowerManagementFramework")}
            </p>
          </div>
          <div className="obj-desc">
            <h3 className="step-sub-title">{convertText("firstName")}</h3>
            <Form.Item
              name="firstName"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: convertText("firstNameRequired")},
                {
                  pattern: validateName(),
                  message: convertText("firstNameValidate")},
                {
                  max: 60,
                  message: `${convertText("userNameMaxLength")}`},
              ]}
            >
              <Input
                size="large"
                placeholder={convertText("firstNamePlaceholder")}
              />
            </Form.Item>
            <h3 className="step-sub-title">{convertText("lastName")}</h3>
            <Form.Item
              name="lastName"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: convertText("lastNameValidate")},
                {
                  pattern: validateName(),
                  message: convertText("lastNameValidate")},
                {
                  max: 60,
                  message: `${convertText("userNameMaxLength")}`},
              ]}
            >
              <Input
                size="large"
                placeholder={convertText("lastNamePlaceholder")}
              />
            </Form.Item>
            <h3 className="step-sub-title">{convertText("email")}</h3>
            <Form.Item
              name="email"
              rules={[
                {
                  pattern: validateEmail(),
                  message: convertText("emailAddressValidate")},
                {
                  required: true,
                  whitespace: true,
                  message: convertText("emailAddressRequired")},
              ]}
            >
              <Input size="large" placeholder={convertText("enterEmail")} />
            </Form.Item>
            <h3 className="step-sub-title">{convertText("division")}</h3>
            <Form.Item
              name="division"
              rules={[
                {
                  required: true,
                  message: `${convertText("pleaseSelectDivision")}`},
              ]}
              initialValue={
                !isCeo
                  ? {
                      value: userDetails?.company_division_id,
                      label: userDetails?.division_name}
                  : null
              }
            >
              <Select
                placeholder={`${convertText("selectCreateDivision")}`}
                className="person-select"
                popupClassName="person-dropdown"
                getPopupContainer={(trigger) => trigger.parentElement}
                onChange={(value) => onDivisionChange(value)}
                value={division}
                disabled={!isCeo}
                // defaultOpen={true}
                onPopupScroll={onScrollOfDivision}
                // eslint-disable-next-line
                dropdownRender={(menu) => (
                  <>
                    <Search
                      placeholder={`${convertText("searchForDivision")}`}
                      allowClear
                      onChange={(e) => onDivisionSearch(e.target.value)}
                      onKeyDown={handlePreventInputKeyDownAction}
                    />
                    {menu}
                    <Divider className="scroll-person-divider" />
                    <Space style={{ padding: "4px 0px 4px" }}>
                      <Input
                        placeholder={`${convertText("pleaseEnterDivision")}`}
                        onChange={(e) => setDivisionName(e.target.value)}
                        value={divisionName}
                        className="add-division-input"
                        maxLength={30}
                      />
                      <Button
                        type="text"
                        icon={<PlusOutlined />}
                        onClick={() => addNewDivision()}
                        disabled={divisionName.trim() === ""}
                        className="add-division-user"
                      >
                        {convertText("addDivision")}
                      </Button>
                    </Space>
                  </>
                )}
              >
                {divisionList.map((divisionData: divisionData) => (
                  <Option key={divisionData?.id} value={divisionData?.id}>
                    <div className="person-list-item">
                      <span>{formatCEOText(divisionData.division.name)}</span>
                    </div>
                  </Option>
                ))}
                {divisionListloading && (
                  <Option>{convertText("loading")}...</Option>
                )}
              </Select>
            </Form.Item>
            <h3 className="step-sub-title">{convertText("role")}</h3>
            <Form.Item
              name="role"
              rules={[
                {
                  required: true,
                  message: convertText("pleaseSelectRole")},
              ]}
            >
              <Select
                placeholder={`${convertText("selectCreateRole")}`}
                className="person-select"
                popupClassName="person-dropdown"
                getPopupContainer={(trigger) => trigger.parentElement}
                onChange={(value) => onRoleChange(value)}
                value={role}
                onPopupScroll={onScrollOfRole}
                // eslint-disable-next-line
                dropdownRender={(menu) => (
                  <>
                    <Search
                      placeholder={convertText("searchForRole")}
                      allowClear
                      onChange={(e) => onRoleSearch(e.target.value)}
                      onKeyDown={handlePreventInputKeyDownAction}
                    />
                    {menu}
                    <Divider className="scroll-person-divider" />

                    <Space style={{ padding: "0 8px 4px" }}>
                      <Input
                        placeholder={`${convertText("pleaseEnterRole")}`}
                        onChange={(e) => setRoleName(e.target.value)}
                        value={roleName}
                        className="add-division-input"
                        maxLength={30}
                      />

                      <Button
                        type="text"
                        icon={<PlusOutlined />}
                        onClick={() => addNewRole()}
                        disabled={roleName === ""}
                        className="add-division-user"
                      >
                        {`${convertText("addRole")}`}
                      </Button>
                    </Space>
                  </>
                )}
              >
                {roleList.map((roleData: IRoleData) => (
                  <Option key={roleData?.role?.id} value={roleData?.role?.id}>
                    <div className="person-list-item">
                      <span>{formatCEOText(roleData.role?.name)}</span>
                    </div>
                  </Option>
                ))}
                {roleListloading && (
                  <Option>{convertText("loading")}...</Option>
                )}
              </Select>
            </Form.Item>
            {isCeo && (
              <>
                <h3 className="step-sub-title">{convertText("reportingTo")}</h3>
                <Form.Item
                  name="person"
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!getFieldValue("division")) {
                          return Promise.reject(
                            new Error(
                              `${convertText(
                                "reportingToDivisionNotSelectedErr"
                              )}`
                            )
                          );
                        }
                        return value
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(`${convertText("pleaseSelectPerson")}`)
                            );
                      }}),
                  ]}
                >
                  <Select
                    placeholder={`${convertText("selectPerson")}`}
                    className="person-select"
                    onChange={(value) => onPersonChange(value)}
                    popupClassName="person-dropdown"
                    getPopupContainer={(trigger) => trigger.parentElement}
                    onClear={() => onPersonSearch("")}
                    value={responsiblePerson}
                    disabled={isReportingToDisabled}
                    // eslint-disable-next-line
                    dropdownRender={(menu) => (
                      <>
                        <Search
                          placeholder={`${convertText("searchForPerson")}`}
                          allowClear
                          onChange={(e) => onPersonSearch(e.target.value)}
                          onKeyDown={handlePreventInputKeyDownAction}
                        />
                        {menu}
                      </>
                    )}
                    onPopupScroll={onScrollOfPerson}
                  >
                    {personList.map((personData: personData) => (
                      <Option key={personData?.id} value={personData?.id}>
                        <div className="person-list-item">
                          <ProfileImage
                            firstName={personData.first_name}
                            lastName={personData.last_name}
                            profileImage={personData?.profile_image}
                            backgroundColor={personData?.profile_color_hash}
                          />
                          <span>
                            {personData.first_name} {personData.last_name}
                          </span>
                        </div>
                      </Option>
                    ))}
                    {personListloading && (
                      <Option>{convertText("loading")}...</Option>
                    )}
                  </Select>
                </Form.Item>
              </>
            )}
            <h3 className="step-sub-title">{convertText("message")}</h3>
            <Form.Item name="message">
              <TextArea
                id="add-objective-description"
                size="large"
                className="user-textarea"
                placeholder={`${convertText("enterMessageOptional")}`}
                autoSize={{
                  minRows: 1,
                  maxRows: 5}}
                maxLength={255}
              />
            </Form.Item>
          </div>
        </div>
      )},
  ];

  return (
    <div>
      <Drawer
        title={`${convertText("addNewUser")}`}
        className="objective-drawer"
        placement="right"
        width={640}
        open={addNewUserOpen}
        maskClosable={false}
        onClose={() => {
          resetStates();
        }}
      >
        <Form form={form} onFinish={handleSubmit}>
          <div className="steps-content user-step-content">
            {steps[0].content}
          </div>
          <div className="steps-btn">
            <Button
              className="self-btn"
              style={{ margin: "0 8px" }}
              onClick={() => {
                resetStates();
              }}
            >
              {convertText("cancelBtn")}
            </Button>
            <Button
              className="theme-btn"
              disabled={formDisabled}
              htmlType="submit"
            >
              {convertText("addUser")}
            </Button>
          </div>
        </Form>
      </Drawer>
    </div>
  );
};

export default AddNewUser;
